/**
 * Billing Status Widget - Shows seller billing summary on dashboard
 * Displays grace period countdown, current period fees, and quick actions
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  CreditCard,
  ChevronRight,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingStatusWidget = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const statusRes = await fetch(`${API_URL}/api/billing/seller/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setBillingStatus(statusData);
        
        // Fetch preview if billing is set up
        if (statusData.enabled && statusData.billing_status !== 'not_setup') {
          const previewRes = await fetch(`${API_URL}/api/billing/seller/preview`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (previewRes.ok) {
            const previewData = await previewRes.json();
            setPreview(previewData);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate days remaining in grace period
  const getGraceDaysRemaining = () => {
    if (!billingStatus?.grace_ends_at) return 0;
    const graceEnd = new Date(billingStatus.grace_ends_at);
    const now = new Date();
    const diff = Math.ceil((graceEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-4">
          <div className="h-20 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  // Don't show widget if billing is not enabled
  if (!billingStatus?.enabled) {
    return null;
  }

  // Not set up - prompt to set up
  if (billingStatus.billing_status === 'not_setup') {
    return (
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium">Set Up Billing</p>
                <p className="text-sm text-muted-foreground">Get 15 days free to start selling</p>
              </div>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/seller/billing')}
              className="gap-1"
            >
              Set Up <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Grace period view
  if (billingStatus.billing_status === 'grace') {
    const daysRemaining = getGraceDaysRemaining();
    const isUrgent = daysRemaining <= 3;
    
    return (
      <Card className={`border-yellow-200 ${isUrgent ? 'bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-950/30 dark:to-yellow-950/30' : 'bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isUrgent ? 'bg-orange-500/10' : 'bg-yellow-500/10'}`}>
                <Clock className={`h-5 w-5 ${isUrgent ? 'text-orange-500' : 'text-yellow-500'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">Grace Period</p>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${isUrgent ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isUrgent ? 'Add payment method soon!' : 'No charges during grace period'}
                </p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/seller/billing')}
              className="gap-1"
            >
              View <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Preview of upcoming charges */}
          {preview && preview.grand_total > 0 && (
            <div className="mt-3 pt-3 border-t border-yellow-200/50 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Estimated first invoice:</span>
              <span className="font-semibold">J${preview.grand_total.toFixed(2)}</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Active billing view
  return (
    <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">Billing Active</p>
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                  {billingStatus.has_payment_method ? 'Card on file' : 'No card'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {preview ? `Current period: J$${preview.grand_total.toFixed(2)}` : 'View billing details'}
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => navigate('/seller/billing')}
            className="gap-1"
          >
            Manage <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Current period breakdown */}
        {preview && (
          <div className="mt-3 pt-3 border-t border-green-200/50 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Listing fees:</span>
              <span className="ml-2 font-medium">J${preview.listing_fee_total.toFixed(2)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Commission:</span>
              <span className="ml-2 font-medium">J${preview.commission_total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingStatusWidget;
